import React from 'react'
import type { TFunction } from 'react-i18next'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import type { Nil, User } from '@pbt/pbt-ui-components'
import { DateUtils, Utils } from '@pbt/pbt-ui-components'

import { PrescriptionItemState } from '~/constants/SOAPStates'
import { fetchMember } from '~/store/actions/members'
import { getPrescriptionStates } from '~/store/reducers/constants'
import { getUser } from '~/store/reducers/users'
import type { Prescription } from '~/types'

import { getPrescriptionMostRecentDate } from './rxCardUtilities'

type UserOrId = string | User | Nil

function getUserId(value: UserOrId): string | null {
  if (!value) return null
  return typeof value === 'string' ? value : (value.id ?? null)
}

function getUserForDisplay(
  value: UserOrId,
  userFromStore?: User,
): User | undefined {
  if (typeof value === 'string' || value == null) {
    return userFromStore
  }
  return value
}

export const ensureDrPrefix = (name: string, t: TFunction): string => {
  const trimmed = (name ?? '').trim()
  if (!trimmed) return trimmed
  if (/^dr\.?\b/i.test(trimmed)) return trimmed

  const dr = t('Clients:TIMELINE.RX_STATUS_TRACKER.DR').trim()
  return `${dr} ${trimmed}`
}

const shouldShowDoctorInfo = (
  enabled: boolean,
  stateName: string | Nil,
  signerId: string | Nil,
): boolean => {
  if (!enabled) return false
  if (stateName === PrescriptionItemState.PENDING_VET_APPROVAL) return false
  return !(stateName === PrescriptionItemState.APPROVED && !signerId)
}

const formatDoctorOnlyDisplay = (
  doctorName: string,
  doctorLabel: string,
): string | undefined => (doctorName ? doctorLabel : undefined)

interface TranslationData {
  approvingUser: User | Nil
  formattedDate: string
  prescriber: User
  status: string
}

const formatWithTranslation = (data: TranslationData, t: TFunction): string => {
  const { status, prescriber, approvingUser, formattedDate } = data

  const prescriberLabel = ensureDrPrefix(Utils.getPersonString(prescriber), t)

  if (approvingUser) {
    const approvingUserLabel = ensureDrPrefix(
      Utils.getPersonString(approvingUser),
      t,
    )
    return t(
      'Clients:TIMELINE.RX_STATUS_TRACKER.STATUS_BY_SIGNER_ON_BEHALF_OF_DOCTOR_ON_DATE',
      {
        status,
        doctor: prescriberLabel,
        signer: approvingUserLabel,
        date: formattedDate,
      },
    )
  }

  return t('Clients:TIMELINE.RX_STATUS_TRACKER.STATUS_BY_DOCTOR_ON_DATE', {
    status,
    doctor: prescriberLabel,
    date: formattedDate,
  })
}

const hasDisplayableName = (u: User | Nil): boolean =>
  Boolean(u && (u.firstName || u.lastName))

export const useDoctorInfoDisplay = ({
  date,
  doctor,
  enabled = true,
  prescription,
  showDoctorOnly = false,
  signer,
  stateId,
}: {
  date?: string | Nil
  doctor?: UserOrId
  enabled?: boolean
  prescription?: Prescription | Nil
  showDoctorOnly?: boolean
  signer?: UserOrId
  stateId?: string | Nil
}): string | undefined => {
  const { t } = useTranslation(['Common', 'Clients'])
  const dispatch = useDispatch()
  const prescriptionStates = useSelector(getPrescriptionStates)

  const prescriberId = getUserId(doctor)
  const approvingUserId = getUserId(signer)

  const prescriberFromStore = useSelector(getUser(prescriberId))
  const approvingUserFromStore = useSelector(getUser(approvingUserId))

  const prescriber = getUserForDisplay(doctor, prescriberFromStore)
  const approvingUser = getUserForDisplay(signer, approvingUserFromStore)

  const requestedUserIdsRef = React.useRef<Set<string>>(new Set())

  const userIdsToFetch = React.useMemo(() => {
    if (!enabled) return []
    return [doctor, signer].filter(
      (v): v is string => typeof v === 'string' && v.length > 0,
    )
  }, [doctor, signer, enabled])

  React.useEffect(() => {
    if (!enabled || userIdsToFetch.length === 0) return

    userIdsToFetch.forEach((id) => {
      const user = id === prescriberId ? prescriber : approvingUser
      if (hasDisplayableName(user) || requestedUserIdsRef.current.has(id))
        return
      requestedUserIdsRef.current.add(id)
      dispatch(fetchMember(id, true))
    })
  }, [
    enabled,
    userIdsToFetch,
    prescriberId,
    dispatch,
    prescriber,
    approvingUser,
  ])

  const stateName = Utils.getConstantName(stateId, prescriptionStates)
  const prescriptionState = prescriptionStates.find((s) => s.id === stateId)
  const status = prescriptionState?.nameTranslation ?? stateName ?? ''

  const displayDate =
    prescription == null
      ? date
      : (getPrescriptionMostRecentDate(prescription) ?? date)
  const formattedDate = displayDate ? DateUtils.formatDate(displayDate) : ''

  if (!shouldShowDoctorInfo(enabled, stateName, approvingUserId))
    return undefined

  const prescriberName = Utils.getPersonString(prescriber)
  const prescriberLabel = ensureDrPrefix(prescriberName, t)

  if (!prescriber || showDoctorOnly) {
    return formatDoctorOnlyDisplay(prescriberName, prescriberLabel)
  }

  if (!status) return undefined

  const prescriberIdForCompare = prescriber.id ?? prescriberId
  const approvingUserIsDifferentPerson =
    approvingUserId != null && approvingUserId !== prescriberIdForCompare

  const approvingUserHasName = hasDisplayableName(approvingUser)
  if (
    approvingUserIsDifferentPerson &&
    !approvingUserHasName &&
    !approvingUser
  ) {
    return undefined
  }

  const approvingUserForDisplay =
    approvingUserIsDifferentPerson && approvingUserHasName
      ? approvingUser
      : null

  return formatWithTranslation(
    {
      status,
      prescriber,
      approvingUser: approvingUserForDisplay,
      formattedDate,
    },
    t,
  )
}
